<script setup lang="ts">
import { CheckCircle, XCircle } from 'lucide-vue-next';
import { computed } from 'vue';

import type { FinancingOperationResult } from '../services/capitalMarketsService';

interface Emits {
  (_e: 'update:modelValue', _value: boolean): void;
}

interface Props {
  modelValue: boolean;
  operationType: 'add' | 'remove';
  results?: FinancingOperationResult[];
}

const props = withDefaults(defineProps<Props>(), {
  results: () => [],
});

const emit = defineEmits<Emits>();

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const title = computed(() => (props.operationType === 'add'
  ? 'Resultado da Adição de Financiamentos'
  : 'Resultado da Remoção de Financiamentos'));

const successCount = computed(() => props.results.filter((r) => r.success).length);

const failureCount = computed(() => props.results.filter((r) => !r.success).length);

function handleClose(): void {
  show.value = false;
}
</script>

<template>
  <div
    class="modal modal-blur fade"
    :class="{ show }"
    :style="{ display: show ? 'block' : 'none' }"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ title }}</h5>
          <button
            type="button"
            class="btn-close"
            @click="handleClose"
          />
        </div>
        <div class="modal-body">
          <div class="mb-3 d-flex gap-3">
            <div class="badge bg-success-lt px-3 py-2">
              <CheckCircle :size="16" class="me-1" />
              {{ successCount }} sucesso(s)
            </div>
            <div v-if="failureCount > 0" class="badge bg-danger-lt px-3 py-2">
              <XCircle :size="16" class="me-1" />
              {{ failureCount }} falha(s)
            </div>
          </div>

          <div v-if="results.length === 0" class="text-muted text-center py-4">
            Nenhum resultado disponível
          </div>

          <div v-else class="table-responsive">
            <table class="table table-vcenter card-table">
              <thead>
                <tr>
                  <th scope="col">ID Financiamento Solfacil</th>
                  <th scope="col">Status</th>
                  <th scope="col">Mensagem</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="result in results" :key="result.solfacil_financing_id">
                  <td>
                    <strong>{{ result.solfacil_financing_id }}</strong>
                  </td>
                  <td>
                    <span
                      v-if="result.success"
                      class="badge bg-success-lt"
                    >
                      <CheckCircle :size="14" class="me-1" />
                      Sucesso
                    </span>
                    <span
                      v-else
                      class="badge bg-danger-lt"
                    >
                      <XCircle :size="14" class="me-1" />
                      Falha
                    </span>
                  </td>
                  <td>{{ result.message }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-primary"
            @click="handleClose"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show" />
</template>
